import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import Logo from "./Logo";
import { EG_NAV } from "../lib/egConstants";
import { trackCtaClick } from "../lib/api";

/** Shared EG header — appears on /eg and every /eg/* sub-page.
 *  Tracks each nav-link click (label "nav-eg-{slug}") so we can show
 *  click counts in the admin dashboard. */
export default function EgHeaderNav({ variant = "absolute" }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  // Always close the drawer on any route change — guards against
  // race conditions where setOpen(false) from a Link's onClick gets
  // dropped (we've seen this on real mobile browsers).
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Navigate imperatively to ensure the route change happens even when
  // the user is already on the same path or React batches updates.
  const go = (to, testid) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(false);
    trackCtaClick(testid);
    if (location.pathname !== to) {
      navigate(to);
    } else {
      // Same path — just close the drawer and scroll to top.
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const positioning =
    variant === "static"
      ? "relative bg-[#0B1D3A]/80 border-b border-[#C5A059]/15"
      : "absolute top-0 inset-x-0 z-30";

  return (
    <header className={`${positioning} px-5 sm:px-10 py-4 sm:py-5`} data-testid="eg-header-nav">
      <div className="max-w-6xl mx-auto flex items-center justify-between gap-4">
        <Link
          to="/eg"
          className="shrink-0"
          data-testid="eg-header-logo"
          onClick={go("/eg", "nav-eg-home")}
        >
          <Logo className="h-10 w-10 sm:h-11 sm:w-11" />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-7 text-sm" dir="rtl">
          {EG_NAV.map((item) => {
            const active = location.pathname === item.to;
            return (
              <Link
                key={item.to}
                to={item.to}
                onClick={go(item.to, item.testid)}
                data-testid={item.testid}
                className={`transition-colors ${
                  active ? "text-[#C5A059] font-bold" : "text-white/85 hover:text-[#C5A059]"
                }`}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* Mobile menu trigger */}
        <button
          onClick={() => setOpen((v) => !v)}
          className="md:hidden text-[#C5A059] p-2 -mr-2"
          aria-label="القائمة"
          data-testid="eg-mobile-menu-btn"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile drawer + click-blocking backdrop. Backdrop closes the
          drawer when the user taps outside any link. */}
      {open && (
        <>
          <button
            type="button"
            aria-label="إغلاق القائمة"
            onClick={() => setOpen(false)}
            className="md:hidden fixed inset-0 z-40 bg-[#040914]/70 backdrop-blur-sm cursor-default"
            data-testid="eg-mobile-menu-backdrop"
          />
          <div
            className="md:hidden absolute top-full inset-x-0 z-50 bg-[#040914] border-t border-[#C5A059]/20 shadow-2xl shadow-black/60"
            dir="rtl"
            data-testid="eg-mobile-menu"
          >
            <nav className="flex flex-col px-6 py-4">
              {EG_NAV.map((item) => {
                const active = location.pathname === item.to;
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={go(item.to, item.testid)}
                    data-testid={`${item.testid}-mobile`}
                    className={`py-3 text-base border-b border-[#C5A059]/10 last:border-0 ${
                      active ? "text-[#C5A059] font-bold" : "text-white/90"
                    }`}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </nav>
          </div>
        </>
      )}
    </header>
  );
}
